import { useState } from "react";
import { motion } from "framer-motion";
import { Instagram } from "lucide-react";
import { toast } from "sonner";
import { revealUp, staggerParent, viewportOnce } from "../../lib/motion";

const API = process.env.REACT_APP_BACKEND_URL;

const SOCIAL = {
  instagram: "https://instagram.com/chickenlabb_official",
  tiktok: "https://www.tiktok.com/@chickenlabb_official",
};

const TikTokIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M16.6 5.82A4.28 4.28 0 0 1 15.54 3h-3.09v12.4a2.59 2.59 0 0 1-2.59 2.5 2.6 2.6 0 0 1-2.6-2.6c0-1.72 1.66-3.01 3.37-2.48V9.66c-3.45-.46-6.47 2.22-6.47 5.64 0 3.33 2.76 5.7 5.69 5.7 3.14 0 5.69-2.55 5.69-5.7V9.01a7.35 7.35 0 0 0 4.3 1.38V7.3s-1.88.09-3.34-1.48Z" />
  </svg>
);

const emptyForm = { name: "", email: "", message: "" };

export const ContactForm = () => {
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (!form.name.trim()) return "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return "Please enter a valid email.";
    if (!form.message.trim()) return "Please enter a message.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      toast.success("Message sent. We'll be in touch soon.");
      setForm(emptyForm);
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputBase =
    "w-full rounded-xl border border-inkred/20 bg-white px-4 py-3 font-body text-sm text-inkred placeholder-inkred/40 outline-none transition-colors focus:border-labred";

  return (
    <motion.form
      onSubmit={handleSubmit}
      data-testid="contact-form"
      initial="hidden"
      whileInView="show"
      viewport={viewportOnce}
      variants={staggerParent}
      className="flex h-full flex-col rounded-[28px] bg-paper px-7 py-10 sm:px-10 sm:py-12"
    >
      <motion.p variants={revealUp} className="mb-2 font-mono text-xs uppercase tracking-[0.3em] text-labred">
        SEND A SIGNAL
      </motion.p>
      <motion.h2 variants={revealUp} className="font-display text-2xl uppercase leading-tight tracking-tight text-inkred sm:text-3xl">
        DROP US A LINE.
      </motion.h2>

      <motion.div variants={revealUp} className="mt-8 space-y-5">
        <div>
          <label htmlFor="cf-name" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-inkred/60">
            Name
          </label>
          <input
            id="cf-name"
            data-testid="contact-name"
            type="text"
            value={form.name}
            onChange={update("name")}
            placeholder="Your name"
            className={inputBase}
          />
        </div>
        <div>
          <label htmlFor="cf-email" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-inkred/60">
            Email
          </label>
          <input
            id="cf-email"
            data-testid="contact-email"
            type="email"
            value={form.email}
            onChange={update("email")}
            placeholder="you@email.com"
            className={inputBase}
          />
        </div>
        <div>
          <label htmlFor="cf-message" className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-inkred/60">
            Message
          </label>
          <textarea
            id="cf-message"
            data-testid="contact-message"
            rows={4}
            value={form.message}
            onChange={update("message")}
            placeholder="Tell us what's on your mind"
            className={`${inputBase} resize-none`}
          />
        </div>
      </motion.div>

      <motion.button
        variants={revealUp}
        type="submit"
        disabled={submitting}
        data-testid="contact-submit"
        className="mt-8 inline-flex items-center justify-center rounded-full bg-labred px-8 py-3.5 font-display text-xs uppercase tracking-wider text-paper transition-colors duration-150 hover:bg-labyellow hover:text-inkred disabled:cursor-not-allowed disabled:opacity-60"
      >
        {submitting ? "Sending..." : "Send Message"}
      </motion.button>

      <motion.div variants={revealUp} className="mt-10 border-t border-inkred/10 pt-8">
        <p className="mb-5 font-display text-2xl uppercase leading-tight tracking-tight text-inkred sm:text-3xl">
          Follow us on social media
        </p>
        <div className="flex items-center gap-4">
          <a
            href={SOCIAL.instagram}
            target="_blank"
            rel="noreferrer"
            aria-label="Follow Chicken Labb on Instagram"
            data-testid="contact-instagram"
            className="flex h-12 w-12 items-center justify-center rounded-full border border-inkred/20 text-inkred transition-colors duration-150 hover:border-labred hover:bg-labred hover:text-paper"
          >
            <Instagram className="h-5 w-5" strokeWidth={2} />
          </a>
          <a
            href={SOCIAL.tiktok}
            target="_blank"
            rel="noreferrer"
            aria-label="Follow Chicken Labb on TikTok"
            data-testid="contact-tiktok"
            className="flex h-12 w-12 items-center justify-center rounded-full border border-inkred/20 text-inkred transition-colors duration-150 hover:border-labred hover:bg-labred hover:text-paper"
          >
            <TikTokIcon className="h-5 w-5" />
          </a>
          <span className="ml-1 font-body text-sm text-inkred/60">@chickenlabb_official</span>
        </div>
      </motion.div>
    </motion.form>
  );
};
